/**
 * 네비게이션 이벤트 유틸리티
 *
 * URL 기반 라우팅 전환 후:
 * - 페이지 이동은 가능하면 useNavigate() 훅을 직접 사용
 * - 이 파일의 이벤트는 훅 외부에서 사용하거나 모달 토글용으로만 사용
 */

import { ROUTES } from '@/config/routes';

// URL 라우트 re-export (컴포넌트에서 직접 사용 가능)
export { ROUTES };

/**
 * 이벤트 기반 네비게이션 (deprecated for URL-based pages)
 *
 * 아래 이벤트들은 모달 토글 또는 레거시 호환용으로만 사용:
 * - openLogoutConfirm, openMapModal, openSettingsModal
 * - toggleCart, closeCart, toggleNotifications, closeNotifications
 *
 * URL 기반 페이지는 useNavigate() 훅 사용 권장
 */
export const navigationEvents = {
  // 로그인/로그아웃 (로그아웃 확인은 모달)
  openLoginPage: () => window.dispatchEvent(new CustomEvent('openLoginPage')),
  openLogoutConfirm: () => window.dispatchEvent(new CustomEvent('openLogoutConfirm')),

  // 프로필 관련 (URL 기반으로 전환됨 - 레거시 호환용)
  openProfilePage: () => window.dispatchEvent(new CustomEvent('openProfilePage')),
  openOrderHistory: () => window.dispatchEvent(new CustomEvent('openOrderHistory')),
  openPrivacySettings: () => window.dispatchEvent(new CustomEvent('openPrivacySettings')),

  // 정보 페이지 (URL 기반으로 전환됨 - 레거시 호환용)
  openAnnouncements: () => window.dispatchEvent(new CustomEvent('openAnnouncements')),
  openTermsOfService: () => window.dispatchEvent(new CustomEvent('openTermsOfService')),
  openPrivacyPolicy: () => window.dispatchEvent(new CustomEvent('openPrivacyPolicy')),
  openPaymentMethodPage: () => window.dispatchEvent(new CustomEvent('openPaymentMethodPage')),

  // 인증 (URL 기반으로 전환됨 - 레거시 호환용)
  navigateToEmailVerification: () => window.dispatchEvent(new CustomEvent('navigateToEmailVerification')),
  navigateToBirthdayVerification: () => window.dispatchEvent(new CustomEvent('navigateToBirthdayVerification')),

  // 지도 (모달 - 상태 기반 유지)
  openMapModal: () => window.dispatchEvent(new CustomEvent('openMapModal')),

  // 설정 (모달 - 상태 기반 유지)
  openSettingsModal: (type?: string) =>
    window.dispatchEvent(new CustomEvent('openSettingsModal', { detail: { type } })),

  // 장바구니 (모달 - 상태 기반 유지)
  toggleCart: () => window.dispatchEvent(new CustomEvent('toggleCart')),
  closeCart: () => window.dispatchEvent(new CustomEvent('closeCart')),

  // 알림 (모달 - 상태 기반 유지)
  toggleNotifications: () => window.dispatchEvent(new CustomEvent('toggleNotifications')),
  closeNotifications: () => window.dispatchEvent(new CustomEvent('closeNotifications')),
} as const;

export type NavigationEventName = keyof typeof navigationEvents;

/**
 * URL 기반 네비게이션 헬퍼
 * 컴포넌트 내부에서 useNavigate()와 함께 사용
 *
 * @example
 * const navigate = useNavigate();
 * navigate(getNavigationPath.profile());
 */
export const getNavigationPath = {
  login: () => ROUTES.LOGIN,
  profile: () => ROUTES.PROFILE,
  orderHistory: () => ROUTES.ORDER_HISTORY,
  paymentMethod: () => ROUTES.PAYMENT_METHOD,
  privacySettings: () => ROUTES.PRIVACY_SETTINGS,
  announcements: () => ROUTES.ANNOUNCEMENTS,
  terms: () => ROUTES.TERMS,
  privacy: () => ROUTES.PRIVACY,
  verifyEmail: () => ROUTES.VERIFY_EMAIL,
  verifyBirthday: () => ROUTES.VERIFY_BIRTHDAY,
} as const;
